import { useEffect, useState, useMemo } from 'react';
import { useInventoryStock } from '../../../hooks/inventory/useInventoryStock';
import { useBranch } from '../../../hooks/branches/useBranch';
import UniversalRecordTable from '../../Tables/universalRecord/UniversalRecordTable';
import { inventoryStockColumns } from '../../../features/inventory/stock/inventoryStockColumns';
import { buildInventoryStockFilters } from '../../../features/inventory/stock/buildInventoryStockFilters';
import { useProductCategories } from '../../../hooks/useProductCategories';
import { useProductUnits } from '../../../hooks/useProductUnits';
import { useProduct } from '../../../hooks/useProduct';
import EditStockSettingsModal from './EditStockSettingsModal';
import { ProductStockDTO } from '../../../types/inventory/ProductStockTypes';
import { FilterConfig } from '../../Tables/advancedRecord/FilterBar';

const InventoryStockTableSection: React.FC = () => {
  const {
    list,
    paginator,
    handlerGetFilteredStock,
    handlerClearFilteredStock,
    handlerOpenEditModal,
  } = useInventoryStock();

  const { fetchBranchDropdown, branchDropdown } = useBranch();
  const { fetchProductCategoriesDropdown, productCategoriesDropdown } =
    useProductCategories();
  const { fetchProductUnitsDropdown, productUnitsDropdown } = useProductUnits();
  const { handlerGetProductsForDropdown, productsDropdown } = useProduct();

  const [filters, setFilters] = useState({});
  const [filtersApplied, setFiltersApplied] = useState(false);

  const pageSize = paginator?.size || 10;
  const currentPage = paginator?.number || 0;

  useEffect(() => {
    fetchBranchDropdown();
    fetchProductCategoriesDropdown();
    fetchProductUnitsDropdown();
    handlerGetProductsForDropdown();
  }, []);

  const columns = useMemo(() => inventoryStockColumns, []);

  // 🔵 Ahora pasamos correctamente los nombres reales
  const filterFields: FilterConfig[] = useMemo(
    () =>
      buildInventoryStockFilters(
        branchDropdown,
        productsDropdown,
        productCategoriesDropdown,
        productUnitsDropdown
      ),
    [branchDropdown, productCategoriesDropdown, productUnitsDropdown]
  );

  const getActions = (row: ProductStockDTO) => {
    const actions = [
      {
        key: 'view',
        label: 'Editar Stock',
        onClick: () => {
          handlerOpenEditModal(row);
        },
      },
    ];

    return actions;
  };

  const buildStockFilterPayload = (filters: any) => {
    const payload: any = {};

    if (filters.branchId) payload.branchId = Number(filters.branchId);
    if (filters.productId) payload.productId = Number(filters.productId); // ✅ usar productId y como número
    if (filters.categoryId) payload.categoryId = Number(filters.categoryId);
    if (filters.unitId) payload.unitId = Number(filters.unitId);

    if (filters.minStockQuantity)
      payload.minStockQuantity = Number(filters.minStockQuantity);

    if (filters.maxStockQuantity)
      payload.maxStockQuantity = Number(filters.maxStockQuantity);

    if (filters.minAverageCost)
      payload.minAverageCost = Number(filters.minAverageCost);

    if (filters.maxAverageCost)
      payload.maxAverageCost = Number(filters.maxAverageCost);

    return payload;
  };

  const handleFilterChange = (newFilters: any) => {
    const payload = buildStockFilterPayload(newFilters);
    setFilters(payload);
    setFiltersApplied(true);
    handlerGetFilteredStock(payload, 0, pageSize);
  };

  const handlePageChange = (page: number) => {
    handlerGetFilteredStock(filters, page, pageSize);
  };

  const handlePageSizeChange = (size: number) => {
    handlerGetFilteredStock(filters, 0, size);
  };

  useEffect(() => {
    if (filtersApplied) {
      handlerGetFilteredStock(filters, currentPage, pageSize);
    }
  }, [currentPage]);

  return (
    <>
      <UniversalRecordTable
        columns={columns}
        data={list}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        filtersConfig={filterFields}
        onFilterChange={handleFilterChange}
        onResetFilters={handlerClearFilteredStock}
        paginator={paginator}
        showPagination={true}
        enableRowClick={true}
        getActions={getActions}
        pageSize={pageSize}
        noDataMessage="Utiliza los filtros por sucursal, producto, categoría, unidad, existencias o costo promedio para refinar tu búsqueda."
      />

      <EditStockSettingsModal />
    </>
  );
};

export default InventoryStockTableSection;
